import { ReactNode } from 'react';
import routes from '@/constants/routes.ts';
import { externalLinks } from '@/constants/externalLinks.ts';
import LuggageIcon from '@/assets/Icons/Spotnana-Luggage-Carry-On.svg?react';
import UsersTravelers from '@/assets/Icons/Spotnana-Users-Travelers.svg?react';
import CompanyIcon from '@/assets/Icons/Spotnana-Company.svg?react';
import DocListIcon from '@/assets/Icons/Spotnana-Doc-List.svg?react';
import StatisticsIcon from '@/assets/Icons/Spotnana-Reports-Statistics.svg?react';
import PolicyDocIcon from '@/assets/Icons/Spotnana-Policy-Document.svg?react';
import BuildingIcon from '@/assets/Icons/Spotnana-Office-Building.svg?react';
import SchedulesIcon from '@/assets/Icons/Spotnana-Calendar.svg?react';
import { Roles } from '@/api/user.ts';

interface MenuLink {
	label: string;
	route: string;
	icon?: ReactNode;
}

interface SubmenuSection {
	header: string;
	links: MenuLink[];
}

export interface HeaderMenu {
	label: string;
	route?: string;
	submenu?: SubmenuSection[];
	match?: string;
}

type RoleBasedMenus = {
	[key in Roles | string]: HeaderMenu[];
};

const headerMenuPerRole: RoleBasedMenus = {
	[Roles.TmcAdmin]: [
		{ label: 'My Climate', route: routes.myClimate, match: routes.myClimate },
		{ label: 'Climate Pro', route: routes.dataCompliancePastTrips, match: routes.climatePro },
		{ label: 'Book', route: externalLinks.spotnana.book },
		{
			label: 'Trips',
			route: externalLinks.spotnana.myTrips,
		},
		{ label: 'Insights', route: externalLinks.spotnana.insights },
		{
			label: 'Programme',
			submenu: [
				{
					header: 'Settings',
					links: [
						{ label: 'Company', route: externalLinks.spotnana.companySettings, icon: <CompanyIcon /> },
						{ label: 'TMC Settings', route: externalLinks.spotnana.tmcSettings, icon: <BuildingIcon /> },
						{ label: 'Users', route: externalLinks.spotnana.usersSettings, icon: <UsersTravelers /> },
						{ label: 'Policies', route: externalLinks.spotnana.policies, icon: <PolicyDocIcon /> },
					],
				},
			],
		},
		{ label: 'Agent', route: externalLinks.spotnana.agentTmc },
		{ label: 'Support', route: externalLinks.spotnana.support },
	],
	[Roles.TmcAgent]: [
		{ label: 'My Climate', route: routes.myClimate, match: routes.myClimate },
		{ label: 'Climate Pro', route: routes.dataCompliancePastTrips, match: routes.climatePro },
		{ label: 'Book', route: externalLinks.spotnana.book },
		{
			label: 'Trips',
			route: externalLinks.spotnana.myTrips,
		},
		{ label: 'Insights', route: externalLinks.spotnana.insights },
		{
			label: 'Analytics',
			submenu: [
				{
					header: 'Reports',
					links: [
						{
							label: 'My reports',
							route: externalLinks.spotnana.myReports,
							icon: <StatisticsIcon />,
						},
						{
							label: 'Company reports',
							route: externalLinks.spotnana.companyReports,
							icon: <DocListIcon />,
						},
						{
							label: 'Schedules',
							route: externalLinks.spotnana.schedules,
							icon: <SchedulesIcon />,
						},
					],
				},
				{
					header: 'Safety',
					links: [
						{
							label: 'Travellers',
							route: externalLinks.spotnana.travelersSafety,
							icon: <LuggageIcon />,
						},
					],
				},
			],
		},
		{
			label: 'Programme',
			submenu: [
				{
					header: 'Settings',
					links: [
						{ label: 'Company', route: externalLinks.spotnana.companySettings, icon: <CompanyIcon /> },
						{ label: 'People', route: externalLinks.spotnana.usersSettings, icon: <UsersTravelers /> },
						{ label: 'Policies', route: externalLinks.spotnana.policies, icon: <PolicyDocIcon /> },
					],
				},
			],
		},
		{ label: 'Agent', route: externalLinks.spotnana.agentTmc },
		{ label: 'Support', route: externalLinks.spotnana.support },
	],
	[Roles.CompanyAdmin]: [
		{ label: 'My Climate', route: routes.myClimate, match: routes.myClimate },
		{ label: 'Climate Pro', route: routes.dataCompliancePastTrips, match: routes.climatePro },
		{ label: 'Book', route: externalLinks.spotnana.book },
		{
			label: 'Trips',
			route: externalLinks.spotnana.myTrips,
		},
		{ label: 'Insights', route: externalLinks.spotnana.insights },
		{
			label: 'Programme',
			submenu: [
				{
					header: 'Settings',
					links: [
						{ label: 'Company', route: externalLinks.spotnana.companySettings, icon: <CompanyIcon /> },
						{ label: 'People', route: externalLinks.spotnana.usersSettings, icon: <UsersTravelers /> },
						{ label: 'Policies', route: externalLinks.spotnana.policies, icon: <PolicyDocIcon /> },
					],
				},
			],
		},
		{ label: 'Support', route: externalLinks.spotnana.support },
	],
	[Roles.CompanyTravelArranger]: [
		{ label: 'My Climate', route: routes.myClimate, match: routes.myClimate },
		{ label: 'Book', route: externalLinks.spotnana.book },
		{
			label: 'Trips',
			route: externalLinks.spotnana.myTrips,
		},
		{
			label: 'Analytics',
			route: externalLinks.spotnana.myReports,
		},
		{ label: 'Support', route: externalLinks.spotnana.support },
	],
	[Roles.TravelArranger]: [
		{ label: 'My Climate', route: routes.myClimate, match: routes.myClimate },
		{ label: 'Book', route: externalLinks.spotnana.book },
		{
			label: 'Trips',
			route: externalLinks.spotnana.myTrips,
		},
		{
			label: 'Insights',
			route: externalLinks.spotnana.insights,
		},
		{ label: 'Support', route: externalLinks.spotnana.support },
	],
	default: [
		{ label: 'My Climate', route: routes.myClimate, match: routes.myClimate },
		{ label: 'Book', route: externalLinks.spotnana.book },
		{ label: 'Trips', route: externalLinks.spotnana.myTrips },
		{ label: 'Analytics', route: externalLinks.spotnana.myReports },
		{ label: 'Support', route: externalLinks.spotnana.support },
	],
};

export const commonLinks = [
	{ to: externalLinks.spotnana.myTrips, label: 'Trips' },
	{ to: externalLinks.spotnana.myApprovals, label: 'Approvals' },
	{ to: externalLinks.spotnana.myProfile, label: 'My Profile' },
	{ to: routes.securitySettings, label: 'Security Settings' },
];

export default headerMenuPerRole;
